"use client";

import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PartnerPerspectives() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: carouselRef,
    isVisible: carouselVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const testimonials = [{
    quote: "Summertime Lane Investments transformed our property sale into a seamless, professional experience. Their integrity and transparency are truly refreshing in this industry.",
    name: "Robert & Sarah Jenkins",
    role: "Homeowners",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/homeowner_testimonial.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/homeowner_testimonial.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/homeowner_testimonial.webp-wvc-sizes"
  }, {
    quote: "Working with the team at Summertime Lane has been a masterclass in responsible investing. They prioritize long-term community value over quick gains.",
    name: "Marcus Thorne",
    role: "Investment Partner",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/partner_testimonial.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/partner_testimonial.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/partner_testimonial.webp-wvc-sizes"
  }, {
    quote: "Their commitment to renovating and improving homes is evident in every project. They don't just buy houses; they build lasting neighborhood legacies.",
    name: "Elena Rodriguez",
    role: "Community Developer",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/community_testimonial.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/community_testimonial.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723050/img/community_testimonial.png-wvc-sizes"
  }];
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);
  useEffect(() => {
    if (!api || isPaused) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 8000);
    return () => clearInterval(interval);
  }, [api, isPaused]);
  return <section data-section-id="3478" id="partner-perspectives" className="relative bg-background text-foreground overflow-hidden py-20 md:py-32">
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse at center, transparent 40%, hsla(43, 45%, 55%, 0.05) 100%)"
    }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div ref={headerRef} className={`max-w-3xl mb-16 md:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="font-mono text-xs md:text-sm tracking-widest text-secondary uppercase mb-6">
            04 / Perspectives
          </div>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
            Voices of
            <br />
            our <em className="italic text-secondary">partners</em>.
          </h2>
        </div>

        <div ref={carouselRef} className={`transition-all duration-1000 ease-out ${carouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          <Carousel setApi={setApi} opts={{
          loop: true,
          duration: 50
        }} className="w-full">
            <CarouselContent>
              {testimonials.map((t, i) => <CarouselItem key={i} data-index={i}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-5 lg:col-span-4">
                      <div className="relative w-full max-w-[320px] aspect-square mx-auto md:mx-0 overflow-hidden rounded-lg shadow-md">
                        <img src={t.image} data-wvc-srcset={t.srcset} data-wvc-sizes={t.sizes} alt={t.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 mix-blend-overlay" style={{
                      background: "linear-gradient(135deg, hsla(43, 45%, 55%, 0.2), hsla(184, 35%, 18%, 0.4))"
                    }} aria-hidden="true" />
                      </div>
                    </div>

                    <div className="md:col-span-7 lg:col-span-8 relative">
                      <Quote className="absolute -top-6 -left-2 md:-top-10 md:-left-6 text-secondary/30" style={{
                    width: 80,
                    height: 80,
                    strokeWidth: 1
                  }} aria-hidden="true" />
                      <blockquote className="relative">
                        <p className="font-serif italic text-2xl md:text-3xl lg:text-[2.5rem] leading-[1.25] text-foreground/95">
                          {t.quote}
                        </p>
                      </blockquote>

                      <div className="mt-10 md:mt-12">
                        <div className="h-px w-16 bg-secondary mb-5" />
                        <div className="font-serif text-lg md:text-xl text-foreground">
                          {t.name}
                        </div>
                        <div className="font-mono text-xs tracking-widest text-secondary uppercase mt-2">
                          {t.role}
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          <div className="mt-14 md:mt-20 flex items-center justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-3" role="tablist">
              {Array.from({
              length: count
            }).map((_, i) => <Button key={i} data-index={i} onClick={() => api?.scrollTo(i)} variant="ghost" size="icon" aria-label={`Go to testimonial ${i + 1}`} className={`h-2 w-2 p-0 rounded-lg transition-all duration-300 ${i === current ? "bg-secondary scale-110" : "border border-secondary/70 bg-transparent hover:bg-secondary/30"}`} />)}
            </div>

            <div className="flex items-center gap-6 md:gap-8 ml-auto">
              <div className="font-mono text-xs tracking-widest text-secondary">
                {String(current + 1).padStart(2, "0")}{" "}
                <span className="text-muted-foreground">/</span>{" "}
                {String(count).padStart(2, "0")}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => api?.scrollPrev()} aria-label="Previous testimonial" className="h-10 w-10 rounded-lg border border-secondary/40 text-secondary hover:bg-secondary/10 hover:text-secondary">
                  <ArrowLeft strokeWidth={1} className="size-5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => api?.scrollNext()} aria-label="Next testimonial" className="h-10 w-10 rounded-lg border border-secondary/40 text-secondary hover:bg-secondary/10 hover:text-secondary">
                  <ArrowRight strokeWidth={1} className="size-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}